import {
  fork,
  take,
  call,
  put,
  cancelled,
  cancel,
  takeLatest,
} from 'redux-saga/effects';
import * as utilsLocalStorageApi from '@capillarytech/cap-ui-utils/utils/utilsLocalStorageApi';
import { getRedirectionUrl } from '@capillarytech/cap-ui-utils/utils/logoutUtil';
import * as Api from '../../../services/api';
import * as types from './constants';
import config from '../../../config/app';
import {
  setAuthenticationDetails,
  removeAuthenticationDetais,
} from '../../../utils/authWrapper';
import { publishCapAuth, failCapAuth } from '../../../utils/capAuthBridge';
function* authorize(user) {
  try {
    const res = yield call(Api.authorize, user);
    yield put({ type: types.LOGIN_SUCCESS, res });
  } catch (error) {
    yield put({ type: types.LOGIN_FAILURE, error });
  } finally {
    if (yield cancelled()) {
      // reducer will clear out login_progress on logout request
      // So this block is not exactly required
    }
  }
}

export function* loginFlow() {
  while (true) {
    const { user } = yield take(types.LOGIN_REQUEST);
    const task = yield fork(authorize, user);
    const action = yield take([types.LOGOUT_REQUEST, types.LOGIN_FAILURE]);
    if (action.type === types.LOGOUT_REQUEST) {
      yield cancel(task);
    }
  }
}

export function* logoutFlow() {
  try {
    const serverLogout = yield call(Api.logout);
    if (serverLogout.success && serverLogout.success === true) {
      const redirectUrl = getRedirectionUrl({
        redirectUri: serverLogout?.redirectUri
      });
      yield [put({ type: types.LOGOUT_SUCCESS })];
      window.location.href = redirectUrl;
    }
  } catch (error) {
    yield put({ type: types.LOGOUT_FAILURE, error });
  }
}

export function* loginSuccess({ res }) {
  setAuthenticationDetails(res);
}

export function* logoutSuccess() {
  removeAuthenticationDetais();
}

export function* switchOrgSuccess({ orgID }) {
  yield call(utilsLocalStorageApi.saveItem, 'orgID', orgID);
}

export function* switchOrg({ orgID, successCallback }) {
  try {
    const res = yield call(Api.changeProxyOrg, orgID);
    if (res.success) yield put({ type: types.SWITCH_ORG_SUCCESS, orgID });
    if (successCallback) successCallback();
  } catch (error) {
    yield put({ type: types.SWITCH_ORG_FAILURE, error });
  }
}

export function* fetchUserInfo({ callback }) {
  try {
    const result = yield call(Api.getUserData);
    const { currentOrgDetails, user: userData, currentOrgId } = result;
    if (currentOrgDetails?.basic_details) {
      if (
        !(
          currentOrgDetails.basic_details.base_language !== '' ||
          currentOrgDetails.basic_details.base_language === null
        )
      ) {
        currentOrgDetails.basic_details.base_language = 'en';
      }
      if (!(currentOrgDetails.basic_details.supported_languages?.length > 0)) {
        currentOrgDetails.basic_details.supported_languages = [
          {
            lang_id: 69,
            language: 'English',
            iso_code: 'en',
          },
        ];
      }
    }

    yield call(utilsLocalStorageApi.saveItem, 'orgID', currentOrgId);
    yield call(utilsLocalStorageApi.saveItem, 'user', userData);
    yield call(utilsLocalStorageApi.saveItem, 'currentOrgDetails', currentOrgDetails);

    // Host is the single owner of the auth call: initialize auth, overload
    // window.capAuth with the full response superset, and resolve the promise
    // that MFE remotes consume via window.getCapAuthData().
    publishCapAuth(result);

    yield put({
      type: types.GET_USER_DATA_SUCCESS,
      userData,
      currentOrgId,
      currentOrgDetails,
    });
    if (callback) {
      callback(userData);
    }
  } catch (error) {
    // Let remotes waiting on window.getCapAuthData() fall through to their own
    // failure handling.
    failCapAuth(error);
    yield put({
      type: types.GET_USER_DATA_FAILURE,
      error,
    });
  }
}

export function* getLastSyncTime() {
  try {
    const res = yield call(Api.getLastSyncTime);
    if (res?.status?.isError === false) {
      yield put({
        type: types.LAST_SYNC_TIME_SUCCESS,
        result: res?.response?.lastsynctime?.date || '',
      });
    } else {
      yield put({
        type: types.LAST_SYNC_TIME_FAILURE,
        error: res?.status?.message || res?.message,
      });
    }
  } catch (error) {
    yield put({ type: types.LAST_SYNC_TIME_FAILURE, error });
  }
}

export function* watchForFetchUserInfo() {
  yield takeLatest(types.GET_USER_DATA_REQUEST, fetchUserInfo);
}

export function* watchForLogoutFlow() {
  yield takeLatest(types.LOGOUT_REQUEST, logoutFlow);
}

export function* watchForLoginSuccess() {
  yield takeLatest(types.LOGIN_SUCCESS, loginSuccess);
}

export function* watchForLogoutSuccess() {
  yield takeLatest(types.LOGOUT_SUCCESS, logoutSuccess);
}

export function* watchForOrgChange() {
  yield takeLatest(types.SWITCH_ORG_REQUEST, switchOrg);
}

export function* watchForOrgChangeSuccess() {
  yield takeLatest(types.SWITCH_ORG_SUCCESS, switchOrgSuccess);
}

export function* watchForGetLastSyncTime() {
  yield takeLatest(types.LAST_SYNC_TIME_REQUEST, getLastSyncTime);
}

export function* getBeamerPostsExplore({ callback }) {
  try {
    const res = yield call(Api.getBeamerPosts, 'EXPLORE');
    if (res?.success) {
      yield put({
        type: types.GET_BEAMER_POSTS_EXPLORE_SUCCESS,
        result: res?.result,
      });
    } else {
      yield put({
        type: types.GET_BEAMER_POSTS_EXPLORE_FAILURE,
        error: res?.error || res?.message,
      });
    }
    if (callback) callback(res);
  } catch (error) {
    yield put({ type: types.GET_BEAMER_POSTS_EXPLORE_FAILURE, error });
  }
}

export function* watchForBeamerPostsExplore() {
  yield takeLatest(
    types.GET_BEAMER_POSTS_EXPLORE_REQUEST,
    getBeamerPostsExplore,
  );
}

export function* getBeamerPostsNotification() {
  try {
    const resNotification = yield call(Api.getBeamerPosts, 'NOTIFICATION');
    const resAlert = yield call(Api.getBeamerPosts, 'ALERT');
    const resWarning = yield call(Api.getBeamerPosts, 'WARNING');

    if (resNotification?.success || resAlert?.success || resWarning?.success) {
      const concatNotifications = [
        ...resNotification?.result,
        ...resAlert?.result,
        ...resWarning?.result,
      ];
      yield put({
        type: types.GET_BEAMER_POSTS_NOTIFICATION_SUCCESS,
        result: concatNotifications,
      });
    } else {
      yield put({
        type: types.GET_BEAMER_POSTS_NOTIFICATION_FAILURE,
        error: '',
      });
    }
  } catch (error) {
    yield put({ type: types.GET_BEAMER_POSTS_NOTIFICATION_FAILURE, error });
  }
}

export function* watchForBeamerPostsNotification() {
  yield takeLatest(
    types.GET_BEAMER_POSTS_NOTIFICATION_REQUEST,
    getBeamerPostsNotification,
  );
}

export default [
  loginFlow,
  watchForLogoutFlow,
  watchForLoginSuccess,
  watchForLogoutSuccess,
  watchForOrgChange,
  watchForFetchUserInfo,
  watchForOrgChangeSuccess,
  watchForGetLastSyncTime,
  watchForBeamerPostsExplore,
  watchForBeamerPostsNotification,
];
